import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import '@/i18n';
import { Toaster } from 'sonner';
import { AuthProvider } from '@/contexts/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';

// Auth Pages
import Login from '@/pages/Login';
import Register from '@/pages/Register';

// Cost Control Pages
import CostControlHome from '@/pages/CostControlHome';
import PhysicalProgress from '@/pages/PhysicalProgress';
import CostAnalysis from '@/pages/CostAnalysis';
import CashFlow from '@/pages/CashFlow';
import ManpowerPage from '@/pages/ManpowerPage';
import DeviationsPage from '@/pages/DeviationsPage';
import SCurvePage from '@/pages/SCurvePage';
import EVMDashboard from '@/pages/EVMDashboard';
import ReportExport from '@/pages/ReportExport';

// Legacy Pages (kept for compatibility)
import AgenticAI from '@/pages/AgenticAI';
import AIChatWidget from '@/pages/AIChatWidget';
import Settings from '@/pages/Settings';

// Layout
import MobileLayout from '@/components/MobileLayout';

function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            
            {/* Standalone AI Chat Widget (no auth required) */}
            <Route path="/ai-chat-widget" element={<AIChatWidget standalone={true} />} />
            <Route path="/embed/ai-chat" element={<AIChatWidget standalone={true} />} />
            
            {/* Cost Control Dashboard Routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <MobileLayout>
                  <CostControlHome />
                </MobileLayout>
              </ProtectedRoute>
            } />
            <Route path="/progress" element={
              <ProtectedRoute>
                <MobileLayout>
                  <PhysicalProgress />
                </MobileLayout>
              </ProtectedRoute>
            } />
            <Route path="/costs" element={
              <ProtectedRoute>
                <MobileLayout>
                  <CostAnalysis />
                </MobileLayout>
              </ProtectedRoute>
            } />
            <Route path="/cash" element={
              <ProtectedRoute>
                <MobileLayout>
                  <CashFlow />
                </MobileLayout>
              </ProtectedRoute>
            } />
            <Route path="/manpower" element={
              <ProtectedRoute>
                <MobileLayout>
                  <ManpowerPage />
                </MobileLayout>
              </ProtectedRoute>
            } />
            <Route path="/deviations" element={
              <ProtectedRoute>
                <MobileLayout>
                  <DeviationsPage />
                </MobileLayout>
              </ProtectedRoute>
            } />
            
            {/* AI Assistant */}
            <Route path="/ai-assistant" element={
              <ProtectedRoute>
                <MobileLayout>
                  <AgenticAI />
                </MobileLayout>
              </ProtectedRoute>
            } />
            
            {/* S-Curve Analysis */}
            <Route path="/s-curve" element={
              <ProtectedRoute>
                <MobileLayout>
                  <SCurvePage />
                </MobileLayout>
              </ProtectedRoute>
            } />
            
            {/* EVM Dashboard */}
            <Route path="/evm" element={
              <ProtectedRoute>
                <MobileLayout>
                  <EVMDashboard />
                </MobileLayout>
              </ProtectedRoute>
            } />
            
            {/* Report Export */}
            <Route path="/export" element={
              <ProtectedRoute>
                <MobileLayout>
                  <ReportExport />
                </MobileLayout>
              </ProtectedRoute>
            } />
            
            {/* Settings */}
            <Route path="/settings" element={
              <ProtectedRoute>
                <MobileLayout>
                  <Settings />
                </MobileLayout>
              </ProtectedRoute>
            } />
            
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </Suspense>
    </AuthProvider>
  );
}

export default App;
